// Resume payloads (kula_contact_resumes) and the contacts auto-created from
// uploaded resumes (kula_resume_parse).

import type {
  BlockOutput,
  ContactCardData,
  ContactListBlockData,
  ResumeBlockData,
  ResumeFile,
} from '../../types';
import { pluralise } from '../pluralise';
import { arr, num, str, strArr, toNum } from './guards';
import type { StructuredContactsCreated, StructuredResume } from './types';

export const buildContactsCreatedBlock = (
  t: StructuredContactsCreated,
  _toolCallId?: string
): BlockOutput => {
  const contacts: ContactCardData[] = (t.contacts ?? []).map((c, i) => ({
    id: String(c.id ?? i),
    name: c.name || 'New contact',
    email: c.email || undefined,
    url: c.url || undefined,
  }));
  const n = contacts.length;
  const summary = `Created ${pluralise(n, 'contact')} from ${
    n === 1 ? 'the resume' : 'the resumes'
  }.`;
  const data: ContactListBlockData = { summary, contacts };
  return { type: 'contactList', data };
};

// parsed_content is the ATS parser's PascalCase digest; map it to our shape.
const toParsed = (pc: Record<string, unknown>): ResumeFile['parsed'] => ({
  name: str(pc.Name),
  currentJobTitle: str(pc.CurrentJobTitle),
  totalExperienceYears: num(pc.TotalExperienceInYears),
  skills: strArr(pc.SkillTags),
  emails: strArr(pc.Emails),
  mobileNumber: str(pc.MobileNumber),
  location: str(pc.Location),
  country: str(pc.Country),
  linkedInUrl: str(pc.LinkedInURL),
  githubUrls: strArr(pc.GithubURL),
  otherUrls: strArr(pc.OtherURLs),
  certifications: strArr(pc.Certifications),
  experience: (arr(pc.Experience) as Record<string, unknown>[]).map((e) => ({
    company: str(e.Company),
    jobTitle: str(e.JobTitle),
    from: str(e.From),
    to: str(e.To) ?? null,
    isCurrent: e.IsCurrentJob === 1 || e.IsCurrentJob === true,
    notes: str(e.Notes),
  })),
  education: (arr(pc.Education) as Record<string, unknown>[]).map((ed) => ({
    school: str(ed.School),
    discipline: str(ed.Discipline),
    qualification: str(ed.Qualification),
    from: str(ed.From),
    to: str(ed.To) ?? null,
  })),
});

// The application id travels alongside the job — without it the FE can only
// deep-link to the job page, not the application's pipeline-stage view.
const toAttachedJobs = (
  raw: unknown
): { id: number; name: string; appId?: number }[] =>
  (arr(raw) as Record<string, unknown>[])
    .map((a) => {
      const job = (a.job ?? {}) as Record<string, unknown>;
      const jobId = toNum(job.id);
      const name = str(job.name) ?? '';
      const appId = toNum(a.id);
      return Number.isFinite(jobId) && name
        ? {
            id: jobId,
            name,
            appId: Number.isFinite(appId) ? appId : undefined,
          }
        : null;
    })
    .filter(
      (x): x is { id: number; name: string; appId?: number } => x !== null
    );

export const buildResumeBlock = (
  t: StructuredResume,
  toolCallId?: string
): BlockOutput | null => {
  const resumes: ResumeFile[] = [];
  for (const r of arr(t.resumes)) {
    if (typeof r !== 'object' || r === null) continue;
    const obj = r as Record<string, unknown>;
    const id = toNum(obj.id);
    const url = str(obj.url) ?? '';
    if (!Number.isFinite(id) || !url) continue;
    resumes.push({
      id,
      url,
      fileName: str(obj.file_name) ?? `Resume ${id}`,
      contentType: str(obj.content_type) ?? 'application/pdf',
      isPrimary: obj.is_primary === true,
      attachedJobs: toAttachedJobs(obj.ats_applications),
      parsed: toParsed((obj.parsed_content ?? {}) as Record<string, unknown>),
    });
  }
  if (resumes.length === 0) return null;
  const contactId = toNum(t.contact_id);
  const data: ResumeBlockData = {
    contactId: Number.isFinite(contactId) ? contactId : 0,
    resumes,
    toolCallId,
  };
  return { type: 'resume', data };
};
